import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { downloadFile } from "../../../util/files";
import { StyledPdfDialog } from "../../styled/PdfDialog";
import PdfTooLargeMessage from "./PdfTooLargeMessage";
import PdfView from "./PdfView";
import PrintWrapper from "./PrintWrapper";

const PdfDialog = ({
  title,
  printTitle = "",
  open,
  handleClose,
  controllers,
  file,
  sendNotificacion,
  paginated = true,
  printable,
  initialScale = 1.5,
  closeButton = false,
  acceptButton = true,
}) => {
  const [totalPages, setTotalPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageScale, setPageScale] = useState(initialScale);
  const [loading, setLoading] = useState(true);
  const [pdfTooLarge, setPdfTooLarge] = useState(false);
  const printWrapperRef = useRef();

  // !!!
  const PDF_LIMITS = {
    MAX_PAGES: 200,
    MAX_ESTIMATED_MEMORY_MB: 2000,
  };

  // Función para determinar si el PDF es demasiado grande
  const shouldShowPdf = (numPages) => {
    const estimatedMemoryMB = numPages * 4; // ~4MB por página renderizada

    if (numPages > PDF_LIMITS.MAX_PAGES) {
      return false;
    }

    if (estimatedMemoryMB > PDF_LIMITS.MAX_ESTIMATED_MEMORY_MB) {
      return false;
    }

    return true;
  };

  function handleZoomIn() {
    if (pageScale < 3) {
      setPageScale(pageScale + 0.2);
    }
  }

  function handleZoomOut() {
    if (pageScale > 0.3) {
      setPageScale(pageScale - 0.2);
    }
  }

  function handleNext() {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1);
    }
  }

  function handlePrevious() {
    if (pageNumber > 0) {
      setPageNumber(pageNumber - 1);
    }
  }

  function handlePdfLoadSuccess(numPages) {
    const isTooLarge = !shouldShowPdf(numPages);
    setPdfTooLarge(isTooLarge);

    if (!isTooLarge) {
      setTotalPages(numPages);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }

  async function handleDownloadPdf() {
    try {
      const byteCharacters = atob(file);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      downloadFile(byteArray, `${printTitle}.pdf`);
    } catch (error) {
      toast.error('Error al descargar PDF');
    }
  }

  useEffect(() => {
    if (!open) {
      setPdfTooLarge(false);
      setLoading(true);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl">
      <StyledPdfDialog.TitleContainer disableTypography>
        <StyledPdfDialog.TitleStyle>
          <Typography variant="h6">{title}</Typography>
        </StyledPdfDialog.TitleStyle>
        {closeButton && (
          <Tooltip title="Cerrar">
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
      </StyledPdfDialog.TitleContainer>
      <StyledPdfDialog.DialogContainer>

        {!loading && controllers && !pdfTooLarge && (
          <StyledPdfDialog.Footer>
            <StyledPdfDialog.ButtonContainer>
              <Button onClick={handleZoomIn} disabled={pageScale >= 3}>
                Zoom +
              </Button>
              <Button onClick={handleZoomOut} disabled={pageScale <= 0.3}>
                Zoom -
              </Button>
            </StyledPdfDialog.ButtonContainer>
            {paginated && (
              <>
                <StyledPdfDialog.PageText>
                  Página {pageNumber} de {totalPages}
                </StyledPdfDialog.PageText>
                <StyledPdfDialog.ButtonContainer>
                  <Button onClick={handlePrevious} disabled={pageNumber === 1}>
                    ‹ Anterior
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={pageNumber === totalPages}
                  >
                    Siguiente ›
                  </Button>
                </StyledPdfDialog.ButtonContainer>
              </>
            )}

            {printable && (
              <StyledPdfDialog.ButtonContainer>
                <Tooltip title="Descargar/Imprimir">
                  <IconButton onClick={printWrapperRef.current?.print}>
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
              </StyledPdfDialog.ButtonContainer>
            )}
          </StyledPdfDialog.Footer>
        )}

        {/* Renderizado condicional limpio */}
        {pdfTooLarge ? (
          <PdfTooLargeMessage onDownload={handleDownloadPdf} />
        ) : !file ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            padding: '60px 40px'
          }}>
            <CircularProgress size={40} thickness={4} />
            <Typography style={{ marginTop: '20px', color: '#6c757d' }}>
              Cargando documento...
            </Typography>
          </div>
        ) : (
          <>
            {/* PrintWrapper separado y condicional */}
            {printable && (
              <PrintWrapper
                reactToPrintProps={{ documentTitle: printTitle }}
                hidden
                ref={printWrapperRef}
              >
                <PdfView pageScale={initialScale} file={file} paginated={false} />
              </PrintWrapper>
            )}

            {/* PdfView principal */}
            <PdfView
              setTotalPagesProps={handlePdfLoadSuccess}
              pageNumberProps={pageNumber}
              pageScale={pageScale}
              file={file}
              setLoadingFalse={() => { }}
              sendNotificacion={sendNotificacion}
              paginated={paginated}
              progressiveRender={!paginated}
              batchSize={50}
              maxPages={200}
            />
          </>
        )}
      </StyledPdfDialog.DialogContainer>
      {acceptButton && (
        <DialogActions style={{ margin: 10 }}>
          <Button onClick={handleClose} color="primary" variant="contained">
            Aceptar
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PdfDialog;
